import { get } from "lodash";
import { IgenomicsState } from "@ispm/core/interfaces/genomics-state.interface";
import { Injectable } from "@angular/core";
import { map, take } from "rxjs/operators";
import { NotificationMessageService } from "@ispm/core/components/customise-notification/notifications-message.service";
import { Observable } from "rxjs";
import { Store } from "@ngrx/store";
import { TestService } from "./test.service";
import { Router } from "@angular/router";
import { RouterManagerService } from "@ispm/core/services/router-manager.service";

@Injectable()
export class WorklistNavigationService {
  private deferReasonChanged: boolean = false;

  constructor(
    private store$: Store<IgenomicsState>,
    private testService: TestService,
    private router: Router,
    private routerManagerService: RouterManagerService,
    private notificationMessageService: NotificationMessageService
  ) {}

  /**
   * Navigates back to worklist page.
   *
   * `canNavigateToWorklist` is a configurable boolean value provided in Test configuration,
   * only those test configurations that has this property value set to `true`
   * along with `latestRevisionStatus as Completed` or `derivedStatus as Defer` is eligible to
   * redirect back to worklist page from current step.
   */
  navigateToWorklist() {
    const testConfiguration = this.testService.testConfiguration;
    const canNavigateToWorklist = get(
      testConfiguration,
      "canNavigateToWorklist",
      false
    );

    if (canNavigateToWorklist) {
      this.navigateToWorklistOnDeferedOrCompleted().pipe(
        map((workflowCompleted) => {
          if (workflowCompleted) {
            const stopRedirectURL = testConfiguration.testActions.find(
              (val) => val.action === "stop"
            );
            if (stopRedirectURL && stopRedirectURL.redirectUrl) {
              // Fix for queryparameters while assign back to next screening cycle via decision capture
              const currentApplication = JSON.parse(sessionStorage.getItem('currentApplication'));
              const redirectUrl = `${stopRedirectURL.redirectUrl}?appname=${currentApplication?.alias}`
              const uriState = this.routerManagerService.urlToState(
                redirectUrl
              );
              // Adding little delay to avoid race condition caused by reload of the page from router manager
              setTimeout(()=>{
                this.router.navigateByUrl(uriState.name);
                this.notificationMessageService.success(
                  "Successfully moved to worklist page"
                );
              },2000);
            }
          }
        })
      ).subscribe();
    }
  }

  forceNavigateToWorklist() {
    //Navigate to the relevnt worklist without checking conditions
    const currentApplication = JSON.parse(sessionStorage.getItem('currentApplication'));
    if (currentApplication && Array.isArray(currentApplication.items) && currentApplication.items[0]) {
      const worklistURL = this.routerManagerService.getStateFromUri(currentApplication.items[0]);
      this.router.navigateByUrl(worklistURL);
    }
    else {
      console.error('Cannot navigate: currentApplication is missing')
    }
  }

  /**
   * Emits a boolean value if the respective Diagnostic Order having derivedStatus as `Defer` or
   * latestRevisionStatus as `Completed`.
   * @returns {Observable<boolean>} An Observable that emits the boolean value
   */
  navigateToWorklistOnDeferedOrCompleted(): Observable<boolean> {
    const diagnostiOrders$ = this.store$
      .select("tests", "diagnosticOrders", "order")
      .filter((order) => !!order);

    return diagnostiOrders$.pipe(
      take(3),
      map((order) => {
        const isDefered = get(order, "derivedStatus.code", "") === "Defer";
        const isCompleted =
          get(order, "workflowRequest.latestRevisionStatus", "") ===
          "Completed";
          if(isDefered && this.deferReasonChanged) {
            return false;
          } else  {
            return isDefered || isCompleted;
          }
      })
    );
  }
  
  /**
   * sets the decision capture reason
   * @param {form} decision capture form value 
   */
  setSelectedFormValue(step: object) {
    this.deferReasonChanged = false;

    if(step != undefined) {
      const decisionCaptureReason = get(step, "priority[0].subType.code", "");
      if(!decisionCaptureReason.toLowerCase().startsWith("defer")){
        this.deferReasonChanged = true;
      }
    }
  }
}
